import React, { useEffect, useState, useCallback, useRef } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import Layout from '../components/Layout';
import { Search, Sun, Snowflake, Leaf, Flower2, Tag } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { tradeColor } from '../utils/tradeColors';
import { HelpTooltip } from '../components/HelpTooltip';
import api from '../utils/api';

const SEASON_ICON = { Summer: Sun, Winter: Snowflake, Fall: Leaf, Spring: Flower2 };

const CampaignCard = ({ c }) => {
  const tc = tradeColor(c.trade);
  return (
  <Link to={`/campaign/${c.id}`} data-testid={`library-card-${c.id}`}
    className={`block rounded-xl border border-[#e0e3d9] bg-white shadow-sm p-5 ${tc.hoverLight} transition-colors`}>
    <div className="flex items-center justify-between mb-2">
      <span className="flex items-center gap-1.5">
        <span className={`text-xs font-bold uppercase tracking-wide px-2 py-0.5 rounded border ${tc.badgeLight}`}>{c.trade}</span>
        <HelpTooltip size={13} text="The trade this campaign is built for. Every ad, script, and page inside is written for this kind of work." testId={`help-trade-${c.id}`} />
      </span>
      <span className="text-xs text-[#6b7063] flex items-center gap-1.5">{c.avg_ticket}
        <HelpTooltip size={13} text="What a homeowner typically pays for this job — so you know what each booked call is worth before spending a dime on ads." testId={`help-ticket-${c.id}`} />
      </span>
    </div>
    <p className="text-[#0c0d0a] font-bold text-lg leading-snug mb-2">{c.campaign_name}</p>
    <div className="flex flex-wrap gap-1.5 items-center">
      {(c.symptom_tags || []).slice(0, 3).map((t) => (
        <span key={t} className="text-[13px] text-[#14160f] bg-[#f0f2ea] border border-[#c9cfbc] rounded-full px-2 py-0.5">{t}</span>
      ))}
      {c.goal_tag && <span className={`text-[13px] border rounded-full px-2 py-0.5 ${tc.goalLight}`}>{c.goal_tag}</span>}
      <HelpTooltip size={13} text="Gray tags = the words a homeowner says when they have this problem (search those words to find this campaign). The colored tag = the campaign's job — emergency calls, replacements, upgrades, or maintenance." testId={`help-tags-${c.id}`} />
    </div>
  </Link>
  );
};

export default function Vault() {
  const { user } = useAuth();
  const [searchParams] = useSearchParams();
  const initialTrade = ['HVAC', 'Plumbing', 'Electrical'].includes(searchParams.get('trade')) ? searchParams.get('trade') : 'All';
  const [query, setQuery] = useState('');
  const [trade, setTrade] = useState(initialTrade);
  const [view, setView] = useState('all');
  const [seasonal, setSeasonal] = useState(null);
  const [allCampaigns, setAllCampaigns] = useState(null);
  const [results, setResults] = useState(null);
  const [searching, setSearching] = useState(false);
  const debounceRef = useRef(null);

  const ents = user?.trade_entitlements?.length && user?.role !== 'admin'
    ? user.trade_entitlements : ['HVAC', 'Plumbing', 'Electrical'];
  const tradeOptions = ['All', ...ents];

  const loadSeasonal = useCallback((t) => {
    api.get(`/library/seasonal${t && t !== 'All' ? `?trade=${t}` : ''}`)
      .then(({ data }) => setSeasonal(data)).catch(() => {});
  }, []);

  const loadAll = useCallback((t) => {
    setAllCampaigns(null);
    api.get(`/campaigns${t && t !== 'All' ? `?trade=${t}` : ''}`)
      .then(({ data }) => setAllCampaigns(data)).catch(() => setAllCampaigns([]));
  }, []);

  useEffect(() => { setTrade(initialTrade); }, [initialTrade]);

  useEffect(() => { loadSeasonal(trade); loadAll(trade); }, [trade, loadSeasonal, loadAll]);

  useEffect(() => {
    clearTimeout(debounceRef.current);
    if (!query.trim()) { setResults(null); setSearching(false); return; }
    setSearching(true);
    debounceRef.current = setTimeout(() => {
      api.get(`/library/search?q=${encodeURIComponent(query)}${trade !== 'All' ? `&trade=${trade}` : ''}`)
        .then(({ data }) => setResults(data.results))
        .catch(() => setResults([]))
        .finally(() => setSearching(false));
    }, 300);
    return () => clearTimeout(debounceRef.current);
  }, [query, trade]);

  const SeasonIcon = SEASON_ICON[seasonal?.season] || Sun;

  return (
    <Layout variant="light">
      <div className="max-w-5xl mx-auto" data-testid="library-page">
        <h1 className="text-3xl sm:text-4xl font-bold text-[#0c0d0a] mb-2">Campaign Library</h1>
        <p className="text-[#4a4f47] mb-8">One library, all trades. Search by season, service, problem, or campaign objective.</p>

        {/* Search bar */}
        <div className="relative mb-5">
          <Search size={20} className="absolute left-4 top-1/2 -translate-y-1/2 text-[#8a8f83]" />
          <input
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder={'Try "no heat", "clogged drain", "panel upgrade"…'}
            data-testid="library-search-input"
            className="w-full bg-white border border-[#d8dbd2] rounded-xl pl-12 pr-4 py-4 text-lg text-[#0c0d0a] placeholder-[#8a8f83] shadow-sm focus:border-[#a8cf3d] focus:outline-none transition-colors"
          />
        </div>

        {/* Trade filter */}
        <div className="flex flex-wrap gap-2 mb-4" data-testid="library-trade-filter">
          {tradeOptions.map((t) => (
            <button key={t} onClick={() => setTrade(t)} data-testid={`trade-filter-${t.toLowerCase()}`}
              className={`px-5 py-2 rounded-full border text-sm font-bold transition-colors ${
                trade === t ? tradeColor(t).chip : 'border-[#d8dbd2] bg-white text-[#4a4f47] hover:border-[#a8cf3d]'}`}>
              {t}
            </button>
          ))}
        </div>

        {/* View tabs: all campaigns vs seasonal */}
        <div className="flex gap-1 mb-10 border-b border-[#e0e3d9]" data-testid="library-view-tabs">
          {[['all', trade === 'All' ? 'All Campaigns' : `All ${trade} Campaigns`], ['seasonal', `${seasonal?.season || 'Seasonal'} Picks`]].map(([v, label]) => (
            <button key={v} onClick={() => setView(v)} data-testid={`library-tab-${v}`}
              className={`px-4 py-2.5 text-sm font-bold -mb-px border-b-2 transition-colors flex items-center gap-1.5 ${
                view === v ? 'border-[#6da410] text-[#0c0d0a]' : 'border-transparent text-[#6b7063] hover:text-[#0c0d0a]'}`}>
              {v === 'seasonal' && <SeasonIcon size={15} className="text-[#6da410]" />}
              {label}
              {v === 'all' && allCampaigns && <span className="text-xs font-semibold text-[#6b7063]">({allCampaigns.length})</span>}
            </button>
          ))}
        </div>

        {results !== null || searching ? (
          <div data-testid="library-results">
            <p className="text-sm text-[#6b7063] mb-4">
              {searching ? 'Searching…' : `${results.length} campaign${results.length === 1 ? '' : 's'} for "${query}"${trade !== 'All' ? ` in ${trade}` : ''}`}
            </p>
            {!searching && results.length === 0 && (
              <p className="text-[#4a4f47]" data-testid="library-no-results">No matches. Try the words a homeowner would use — like "no heat" or "leak".</p>
            )}
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {(results || []).map((c) => <CampaignCard key={c.id} c={c} />)}
            </div>
          </div>
        ) : view === 'seasonal' ? (
          <div data-testid="library-seasonal">
            <div className="flex items-center gap-2 mb-1">
              <SeasonIcon size={20} className="text-[#6da410]" />
              <h2 className="text-xl font-bold text-[#0c0d0a]">{seasonal?.season || ''} Suggestions{trade !== 'All' ? ` — ${trade}` : ''}</h2>
            </div>
            <p className="text-sm text-[#6b7063] mb-5">What's making phones ring right now. Search above to find anything else.</p>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {(seasonal?.results || []).map((c) => <CampaignCard key={c.id} c={c} />)}
            </div>
          </div>
        ) : (
          <div data-testid="library-all">
            <p className="text-sm text-[#6b7063] mb-5">
              {allCampaigns === null
                ? 'Loading campaigns…'
                : `Every ${trade === 'All' ? '' : trade + ' '}campaign in your library. Type in the search bar to narrow it down.`}
            </p>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {(allCampaigns || []).map((c) => <CampaignCard key={c.id} c={c} />)}
            </div>
            <div className="mt-10 flex items-center gap-2 text-sm text-[#6b7063]">
              <Tag size={14} className="text-[#6da410]" />
              <span>Tagged by trade, season, symptom, and goal — search with the words a homeowner would use.</span>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
}
